import { Button, Chip } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import { FC, useCallback, useMemo, useState } from 'react'
import ApiStateView from '../ApiState'
import BackButton from '../BackButton'
import LogiTable from '../LogiTable'
import Panel from '../Panel'
import SectionTitle from '../SectionTitle'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { useUserId } from '../../hooks/useUserId'
import { suppliesApi, SupplyRequestDto } from '../../api'
import { TERMINO_ETIQUETA_LOWER } from '../../constants'
import { ToneKey } from '../../models'

const INSUMO_LABEL: Record<string, string> = {
  caja: 'Cajas', bolsa_a: 'Bolsas tipo A', bolsa_b: 'Bolsas tipo B', sello: 'Sellos'
}

const urgenciaPill = (r: SupplyRequestDto): { tone: ToneKey, label: string } =>
  r.prioridad === 'critica' ? { tone: 'rosa', label: 'Crítico' } : { tone: 'amber', label: 'Urgente' }

// Tono interno → color de Chip de NextUI.
const chipColor = (tone: ToneKey): 'success' | 'warning' | 'danger' | 'primary' | 'secondary' | 'default' => {
  switch (tone) {
    case 'green': return 'success'
    case 'amber': return 'warning'
    case 'rosa': return 'danger'
    case 'blue': return 'primary'
    case 'lila': return 'secondary'
    default: return 'default'
  }
}

const columns = [
  { name: 'Tienda', uid: 'store' },
  { name: 'Insumo', uid: 'insumo' },
  { name: 'Stock actual', uid: 'stock_actual', align: 'center' as const },
  { name: 'Safety stock', uid: 'safety_stock', align: 'center' as const },
  { name: 'Cantidad sugerida', uid: 'cantidad_sugerida', align: 'center' as const },
  { name: 'Urgencia', uid: 'urgencia' },
  { name: '', uid: 'actions' }
]

const TodasAutomaticasView: FC = () => {
  const idSeller = useSelectedSeller()
  const userId = useUserId()
  const [busy, setBusy] = useState(false)

  const data = useApi(
    useCallback(async () => await suppliesApi.requests(idSeller, { origen: 'automatica', estado: 'pendiente' }), [idSeller]),
    [idSeller]
  )

  const rows = useMemo(() => data.data ?? [], [data.data])

  const approve = useCallback(async (id: number) => {
    setBusy(true)
    try {
      const r = await suppliesApi.approveRequest(id, idSeller, userId)
      enqueueSnackbar(r.estado === 'aprobada' ? `Solicitud ${id} aprobada` : `Con error al generar ${TERMINO_ETIQUETA_LOWER}: ${r.error_message ?? 'error'}`, { variant: r.estado === 'aprobada' ? 'success' : 'warning' })
      await data.reload()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo aprobar', { variant: 'error' })
    } finally { setBusy(false) }
  }, [idSeller, userId, data])

  const reject = useCallback(async (id: number) => {
    setBusy(true)
    try {
      await suppliesApi.rejectRequest(id, idSeller, userId)
      enqueueSnackbar(`Solicitud ${id} rechazada`, { variant: 'success' })
      await data.reload()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo rechazar', { variant: 'error' })
    } finally { setBusy(false) }
  }, [idSeller, userId, data])

  const approveAll = useCallback(async () => {
    if (rows.length === 0) { enqueueSnackbar('No hay solicitudes pendientes', { variant: 'info' }); return }
    setBusy(true)
    let ok = 0; let fail = 0
    for (const r of rows) {
      try { await suppliesApi.approveRequest(r.id_request, idSeller, userId); ok++ } catch { fail++ }
    }
    enqueueSnackbar(`Aprobadas ${ok}${fail > 0 ? ` · ${fail} fallaron` : ''}`, { variant: fail > 0 ? 'warning' : 'success' })
    await data.reload()
    setBusy(false)
  }, [rows, idSeller, userId, data])

  const renderCell = (r: SupplyRequestDto, columnKey: string): JSX.Element => {
    const it = r.items[0]
    switch (columnKey) {
      case 'store':
        return <span className='font-medium text-xs'>{r.store}</span>
      case 'insumo':
        return <span className='text-xs'>{it !== undefined ? (INSUMO_LABEL[it.codigo] ?? it.codigo) : '—'}</span>
      case 'stock_actual':
        return <span className='text-xs'>{it?.stock_actual_snapshot ?? '—'}</span>
      case 'safety_stock':
        return <span className='text-xs'>{it?.safety_stock_snapshot ?? '—'}</span>
      case 'cantidad_sugerida':
        return <span className='text-xs font-semibold'>{it?.cantidad_sugerida ?? it?.cantidad_solicitada ?? '—'}</span>
      case 'urgencia': {
        const pill = urgenciaPill(r)
        return <Chip size='sm' variant='flat' color={chipColor(pill.tone)}>{pill.label}</Chip>
      }
      case 'actions':
        return (
          <div className='flex gap-2 justify-end items-center whitespace-nowrap'>
            <Button size='sm' isDisabled={busy} onPress={() => { void approve(r.id_request) }} className='bg-black text-white text-xs h-8'>Aprobar</Button>
            <Button size='sm' variant='flat' isDisabled={busy} onPress={() => { void reject(r.id_request) }} className='text-xs h-8'>Rechazar</Button>
          </div>
        )
      default:
        return <span className='text-xs'>{(r as any)[columnKey]}</span>
    }
  }

  return (
    <div className='space-y-4'>
      <BackButton to='insumos' />

      <Panel>
        <div className='flex items-center justify-between mb-4 flex-wrap gap-3'>
          <SectionTitle
            title={`Solicitudes automáticas · ${rows.length} pendientes`}
            description='Generadas por el chequeo de safety stock · cuando un insumo baja del umbral el sistema crea la solicitud.'
          />
          <div className='flex gap-2'>
            <Button isDisabled={busy} onPress={() => { void approveAll() }} className='bg-[#22c55e] text-white text-xs font-semibold'>Aprobar todas</Button>
          </div>
        </div>

        <ApiStateView
          loading={data.loading} error={data.error} onRetry={data.reload}
        >
          <LogiTable
            columns={columns}
            items={rows}
            renderCell={renderCell}
            rowKey={(r) => r.id_request}
            ariaLabel='Solicitudes automáticas'
            loading={data.loading}
            emptyText='Sin solicitudes automáticas pendientes'
          />
        </ApiStateView>
      </Panel>
    </div>
  )
}

export default TodasAutomaticasView
